import React, { ReactNode, useContext } from "react";
import { Auth, Amplify } from "aws-amplify";
import { OnUnauthorizedAccess, RemoveUnauthorizedAccess } from "@emisgroup/cohort-builder-kit";
import { CognitoUser } from "@aws-amplify/auth";
import AwsConfig from "./AwsConfig";
import { axiosInstance } from "./utilities/HttpRequest/AxiosConfig";

export type User  = {
  username: string;
  token: string | undefined;
  expiration: number | undefined;
};

interface IAuthContext {
  user: User | null;
  logout(): Promise<any>;
}

interface IProps {
  children?: ReactNode;
}

/**
 * To logout the user
 */
export const logout = async (): Promise<any> => {
  try {
    await Auth.signOut();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error during logout:", error);
  }
};

export const AuthContext = React.createContext<IAuthContext>({
  user: null,
  logout
});

export const useAuth = (): IAuthContext => useContext(AuthContext);

export const AuthProvider: React.FC<IProps> = ({ children }: IProps) => {
  const [user, setUser] = React.useState<User|null>(null);
  Amplify.configure(AwsConfig());

  React.useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((cognitoUser: CognitoUser) => {
        const session = cognitoUser.getSignInUserSession();
        setUser({
          username: cognitoUser.getUsername(),
          token: session?.getAccessToken().getJwtToken(),
          expiration: session?.getIdToken().getExpiration()
        });
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
      })
      .catch(() => {
        Auth.federatedSignIn().then().catch(() => {
          setUser(null);
        });
      });

    // To handle unauthorized access
    OnUnauthorizedAccess(logout);

    return () => {
      RemoveUnauthorizedAccess(logout);
    };
  }, []);

  const values = React.useMemo(() => {
    axiosInstance(user?.token);
    return { user, logout };
  }, [user]);

  return (
    <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
  );
};

export const useUser = (): any => {
  const context = React.useContext(AuthContext);

  if(context === undefined) {
    throw new Error("`useUser` must be used within a `UserProvider` component");
  }
  return context;
};
